import API from '../context/API';
import type {
  ApiEnvelope,
  Attribution,
  AttributionParams,
  CreateCreatorPayload,
  CreatePayoutPayload,
  CreateTermsPayload,
  Creator,
  CreatorPerformance,
  CreatorTerms,
  LeaderboardResponse,
  PaginatedResponse,
  PaginationParams,
  Payout,
  PayoutExportParams,
  PayoutParams,
  PerformanceParams,
  UpdateCreatorPayload,
  UpdatePayoutPayload,
} from '../components/CreatorProgram/types';

/**
 * Creator Program — merchant dashboard API.
 *
 * Mirrors `fe-integration-guide-creator-merchant-dashboard.md` §2–§5, one function
 * per documented endpoint. Nothing here is invented: if the docs don't describe an
 * endpoint, it does not appear.
 *
 * Notes that shape this module:
 * - Auth: the shared `API` instance already attaches `Authorization: Bearer <jwt>`
 *   via its request interceptor, so no call sets that header itself.
 * - The merchant is resolved from the JWT; none of these routes take a merchant id.
 * - Every response is wrapped `{ success, data }`, and list endpoints nest one level
 *   deeper as `{ success, data: { data, total, page, limit } }`. Both wrappers are
 *   unwrapped here so no caller ever sees them.
 */

const CREATORS = '/merchant/creators';
const ATTRIBUTIONS = '/merchant/attributions';
const PAYOUTS = '/merchant/payouts';

export const CREATOR_PROGRAM_ENDPOINTS = {
  CREATORS,
  CREATOR: (id: string) => `${CREATORS}/${id}`,
  CREATOR_TERMS: (id: string) => `${CREATORS}/${id}/terms`,
  CREATOR_PERFORMANCE: (id: string) => `${CREATORS}/${id}/performance`,
  LEADERBOARD: `${CREATORS}/performance`,
  ATTRIBUTIONS,
  PAYOUTS,
  PAYOUT: (id: string) => `${PAYOUTS}/${id}`,
  PAYOUTS_EXPORT: `${PAYOUTS}/export`,
} as const;

/** Unwrap `{ success, data }`. */
const unwrap = <T,>(body: ApiEnvelope<T> | T): T => {
  const envelope = body as ApiEnvelope<T>;
  if (envelope && typeof envelope === 'object' && 'data' in envelope && 'success' in envelope) {
    return envelope.data;
  }
  return body as T;
};

/**
 * Strip `undefined` / empty-string params so we never send `?status=` and
 * accidentally filter on an empty value.
 */
const cleanParams = (params: Record<string, unknown> = {}): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

export const creatorProgramAPI = {
  // -------------------------------------------------------------------------
  // Creators — §2
  // -------------------------------------------------------------------------

  /** §2.2 — list creators. Each row's `terms` is full version history, not current terms. */
  listCreators: async (params: PaginationParams = {}): Promise<PaginatedResponse<Creator>> => {
    const response = await API.get(CREATOR_PROGRAM_ENDPOINTS.CREATORS, {
      params: cleanParams({ ...params }),
    });
    return unwrap<PaginatedResponse<Creator>>(response.data);
  },

  /** §2.3 — one creator with `terms` (full history) and `codes` attached. 404 if not ours. */
  getCreator: async (id: string): Promise<Creator> => {
    const response = await API.get(CREATOR_PROGRAM_ENDPOINTS.CREATOR(id));
    return unwrap<Creator>(response.data);
  },

  /** §2.1 — create. Response never includes `password`. */
  createCreator: async (payload: CreateCreatorPayload): Promise<Creator> => {
    const response = await API.post(CREATOR_PROGRAM_ENDPOINTS.CREATORS, payload);
    return unwrap<Creator>(response.data);
  },

  /**
   * §2.4 — update status and/or code prefix. Both fields are optional and
   * independent; send only what changed. No other field is updatable today.
   */
  updateCreator: async (id: string, payload: UpdateCreatorPayload): Promise<Creator> => {
    const response = await API.patch(CREATOR_PROGRAM_ENDPOINTS.CREATOR(id), payload);
    return unwrap<Creator>(response.data);
  },

  /**
   * §2.5 — set new commission terms. Always creates a brand-new row and supersedes
   * the previous current one; there is deliberately no update counterpart.
   */
  createTerms: async (id: string, payload: CreateTermsPayload): Promise<CreatorTerms> => {
    const response = await API.post(CREATOR_PROGRAM_ENDPOINTS.CREATOR_TERMS(id), payload);
    return unwrap<CreatorTerms>(response.data);
  },

  // -------------------------------------------------------------------------
  // Performance — §3
  // -------------------------------------------------------------------------

  /**
   * §3.1 — leaderboard across all creators. Already sorted by
   * `summary.netCreatorEarnings` DESC then paginated; render in the order received.
   */
  getLeaderboard: async (params: PerformanceParams = {}): Promise<LeaderboardResponse> => {
    const response = await API.get(CREATOR_PROGRAM_ENDPOINTS.LEADERBOARD, {
      params: cleanParams({ ...params }),
    });
    return unwrap<LeaderboardResponse>(response.data);
  },

  /**
   * §3.2 — one creator's numbers over a range. Returns everything one screen needs
   * (summary, byStore, byCode, links). `earnings.*` in the response are window-scoped,
   * not the creator's true balance.
   */
  getCreatorPerformance: async (
    id: string,
    params: PerformanceParams = {}
  ): Promise<CreatorPerformance> => {
    const response = await API.get(CREATOR_PROGRAM_ENDPOINTS.CREATOR_PERFORMANCE(id), {
      params: cleanParams({ ...params }),
    });
    return unwrap<CreatorPerformance>(response.data);
  },

  // -------------------------------------------------------------------------
  // Attributions — §4
  // -------------------------------------------------------------------------

  /** §4 — order-level ledger. All filters combine with AND, not OR. */
  listAttributions: async (
    params: AttributionParams = {}
  ): Promise<PaginatedResponse<Attribution>> => {
    const response = await API.get(CREATOR_PROGRAM_ENDPOINTS.ATTRIBUTIONS, {
      params: cleanParams({ ...params }),
    });
    return unwrap<PaginatedResponse<Attribution>>(response.data);
  },

  // -------------------------------------------------------------------------
  // Payouts — §5
  // -------------------------------------------------------------------------

  /** §5.2 — list payouts. */
  listPayouts: async (params: PayoutParams = {}): Promise<PaginatedResponse<Payout>> => {
    const response = await API.get(CREATOR_PROGRAM_ENDPOINTS.PAYOUTS, {
      params: cleanParams({ ...params }),
    });
    return unwrap<PaginatedResponse<Payout>>(response.data);
  },

  /** §5.3 — payout detail, including the exact `attributions[]` it swept up. */
  getPayout: async (id: string): Promise<Payout> => {
    const response = await API.get(CREATOR_PROGRAM_ENDPOINTS.PAYOUT(id));
    return unwrap<Payout>(response.data);
  },

  /**
   * §5.1 — draft a payout. Sweeps every currently-`payable` row for the creator
   * created before `periodEnd` (`periodStart` is bookkeeping only, not a query bound).
   * Throws `422` with `code: "non_positive_payout_balance"` when there was nothing
   * to draft — read the balance off the error body rather than showing a generic error.
   */
  createPayout: async (payload: CreatePayoutPayload): Promise<Payout> => {
    const response = await API.post(CREATOR_PROGRAM_ENDPOINTS.PAYOUTS, payload);
    return unwrap<Payout>(response.data);
  },

  /**
   * §5.4 — advance a payout's status. Legal transitions only: `draft → approved`,
   * `approved → paid` (with `reference`), and `draft|approved → cancelled`. Re-sending
   * the status a payout already has is a no-op success, so submits are retry-safe.
   */
  updatePayout: async (id: string, payload: UpdatePayoutPayload): Promise<Payout> => {
    const response = await API.patch(CREATOR_PROGRAM_ENDPOINTS.PAYOUT(id), payload);
    return unwrap<Payout>(response.data);
  },

  /**
   * §5.5 — CSV export. Streamed as `text/csv` with `Content-Disposition: attachment`,
   * and the JWT still has to travel in the `Authorization` header, so this is fetched
   * as an authenticated blob rather than opened as a public URL.
   */
  exportPayouts: async (params: PayoutExportParams = {}): Promise<Blob> => {
    const response = await API.get(CREATOR_PROGRAM_ENDPOINTS.PAYOUTS_EXPORT, {
      params: cleanParams({ ...params }),
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};

export default creatorProgramAPI;
